import { ReactComponent as AlertIcon } from '../../assets/images/circle-exclaimation-icon.svg';
import { SUPPORTED_STACKS_SENTENCE } from './stackLogos';

interface NoAppDetectedNoticeProps {
  // The folder that was inspected, shown so the message is about a place and
  // not about the repository as a whole.
  rootDirectoryPath: string;
  onBrowseRepository: () => void;
  // With the buildpack route off, a Dockerfile is the only way in — listing the
  // stacks we build would be advertising something this environment cannot do.
  buildpacksAvailable?: boolean;
}

// Shown when the detection found nothing to build in the selected folder.
//
// A warning, not a block: the detection only predicts what the builder will
// decide, so a false negative must not stop a deploy that would have worked.
// That is why it is a hairline and an icon rather than a filled red panel.
//
// The lead names the stacks instead of the files. The first question this
// state raises is "is my technology supported at all?", and a list of six
// filenames answers it badly — it reads like the whole of what we accept. The
// filenames still have to appear, because they are what the user can actually
// check, but they belong in the how, not in the headline.
const NoAppDetectedNotice = ({
  rootDirectoryPath,
  onBrowseRepository,
  buildpacksAvailable,
}: NoAppDetectedNoticeProps) => (
  <div className="flex items-start gap-3.5 text-ds-135 leading-[1.55]">
    <span className="w-[30px] h-[30px] flex-none rounded-full bg-state-err/[0.14] text-state-err flex items-center justify-center">
      <AlertIcon width="16" height="16" aria-hidden="true" />
    </span>
    {/* Wide enough not to bind at the form's usual width — it is a ceiling for
        wide viewports, where an uncapped line runs past where the eye tracks
        reliably. Capped on the text and not on the panel: a narrow box
        floating in a wide form looks like a mistake rather than a choice. */}
    <div className="min-w-0 max-w-[90ch]">
      <div className="font-semibold">No app detected in "{rootDirectoryPath}".</div>
      <div className="mt-1 opacity-90">
        {buildpacksAvailable === false
          ? 'Apps here are built from a Dockerfile, and this folder has none.'
          : `Back4app builds ${SUPPORTED_STACKS_SENTENCE} — none of them turned up here.`}
      </div>
      <ul className="list-disc ml-4 mt-3 space-y-2 opacity-90">
        <li>
          {buildpacksAvailable === false
            ? 'The Dockerfile has to sit at the root of this folder.'
            : "Your stack's manifest has to sit at the root of this folder — " +
              'package.json, requirements.txt, go.mod, pom.xml, composer.json, ' +
              'Gemfile, and so on — or a Dockerfile.'}
        </li>
        <li>
          Your app is in a subfolder?{' '}
          <button
            type="button"
            className="underline underline-offset-2"
            onClick={onBrowseRepository}
          >
            Pick another folder
          </button>
          .
        </li>
      </ul>
    </div>
  </div>
);

export default NoAppDetectedNotice;
